<template>
  <ViewContainer :title="$t('common.profile')">
    <v-row class="d-flex flex-wrap ma-0 pa-0">
      <v-col cols="12" class="px-0">
        <v-sheet rounded elevation="2" class="pt-4 mb-4">
          <AccountSummary
            :isLoading="isLoading"
            :profileAccount="profileAccount"
            :isSelfProfile="isSelfProfile"
            @photoUploaded="updateAccountPhoto"
          />
          <v-tabs grow v-model="selectedTab" @change="trackNavigate">
            <v-tab
              v-for="tab in tabs"
              active-class="blue-grey--text text--darken-3"
              class="blue-grey--text text--darken-2"
              :key="tab.id"
              >{{ tab.title }}</v-tab
            >
          </v-tabs>
        </v-sheet>
      </v-col>
    </v-row>

    <v-window v-model="selectedTab">
      <v-window-item :key="TABS.ABOUT">
        <AboutTab
          :isSelfProfile="isSelfProfile"
          :profileAccount="profileAccount"
          :fetchingProfile="isLoading"
        />
      </v-window-item>

      <v-window-item :key="TABS.HIGHLIGHTS">
        <HighlightsTab
          :organizationId="organizationId"
          :activity="activity"
          :stats="stats"
          :isSelfProfile="isSelfProfile"
          :profileAccount="profileAccount"
          :isLoading="isLoading"
        />
      </v-window-item>

      <v-window-item :key="TABS.ACTIVITY">
        <ActivityTab
          :organizationId="organizationId"
          :isSelfProfile="isSelfProfile"
          :user="profileAccount"
          :isLoading="isLoading"
        />
      </v-window-item>

      <v-window-item :key="TABS.RECOGNITIONS">
        <RecognitionsTab
          :organizationId="organizationId"
          :isSelfProfile="isSelfProfile"
          :profileAccount="profileAccount"
          :isLoading="isLoading"
        />
      </v-window-item>
    </v-window>
  </ViewContainer>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import _ from 'lodash'

import mixin from '@/mixins/mixin.vue'
import trackerMixin from '@/mixins/tracking/trackerMixin.vue'

import ViewContainer from '@/components/Core/Layout/ViewContainer.vue'

import AccountSummary from './AccountSummary.vue'
import AboutTab from './AboutTab.vue'
import HighlightsTab from './HighlightsTab.vue'
import RecognitionsTab from './RecognitionsTab.vue'
import ActivityTab from '@/containers/ProfileView/ActivityTab/ActivityTab.vue'

const TABS = {
  ABOUT: 0,
  HIGHLIGHTS: 1,
  ACTIVITY: 2,
  RECOGNITIONS: 3,
}

export default {
  name: 'ProfileView',

  mixins: [mixin, trackerMixin],

  components: {
    AccountSummary,
    ViewContainer,
    AboutTab,
    HighlightsTab,
    RecognitionsTab,
    ActivityTab,
  },

  props: {
    organizationId: {
      required: true,
      type: String,
    },
    userId: {
      required: true,
      type: String,
    },
  },

  data() {
    return {
      TABS,
      selectedTab: 0,
      profile: {},
      isLoading: true,
      previousPage: null,
      tabs: [
        { id: 'about', title: this.$t('common.about') },
        { id: 'highlights', title: this.$t('profile.highlight_other') },
        { id: 'activity', title: this.$t('common.activity') },
        {
          id: 'recognitions',
          title: this.$t('recognitions.recognition_other'),
        },
      ],
    }
  },

  beforeRouteEnter(to, from, next) {
    next(vm => {
      vm.previousPage = from?.name ?? from?.path
    })
  },

  watch: {
    async $route(to, from) {
      this.previousPage = from?.name ?? from?.path
      this.trackUserProfileView(this.userId, this.previousPage)
      if (from.params.userId !== to.params.userId) {
        this.getProfile()
      }
    },

    selectedTab(value) {
      this.trackProfileNavigate({ section: this.tabs[value.id] })
    },
  },

  async mounted() {
    this.trackUserProfileView(this.userId, this.previousPage)
    await this.getProfile()
  },

  computed: {
    ...mapGetters(['accountId']),

    loggedUserId() {
      return this.accountId
    },

    isSelfProfile() {
      return Boolean(this.loggedUserId === this.userId)
    },

    profileAccount() {
      return this.profile?.account ?? {}
    },

    activity() {
      return this.profile?.activity ?? {}
    },

    stats() {
      return this.profile?.stats ?? {}
    },
  },

  methods: {
    ...mapActions({
      fetchProfile: 'profile/getProfile',
      updateAccount: 'updateOwnAccount',
    }),

    trackNavigate(tab) {
      const selectedTab = Object.keys(this.TABS).find(
        key => this.TABS[key] === tab
      )
      this.trackProfileNavigate({ element: selectedTab, section: 'tabs' })
    },

    async getProfile() {
      try {
        this.isLoading = true
        this.profile = await this.fetchProfile({
          organizationId: this.organizationId,
          accountId: this.userId,
        })
      } catch (err) {
        this.checkNotFoundError(err)
        this.handleRequestError(err)
      } finally {
        this.isLoading = false
      }
    },

    async updateAccountPhoto(photo) {
      try {
        this.isLoading = true
        await this.updateAccount({
          organizationId: this.organizationId,
          userId: this.userId,
          data: {
            ...this.profileAccount,
            photo: _.isEmpty(photo) ? null : photo.id,
          },
        })

        await this.getProfile()

        this.showSuccess(this.$t('profile_settings.successfully_saved'))
        this.trackProfileNavigate({
          element: 'changePhoto',
          section: 'summary',
        })
      } catch (err) {
        this.handleRequestError(err)
      } finally {
        this.isLoading = false
      }
    },
  },
}
</script>
